import React from "react";
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { Input, Checkbox, FormGroup } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import { FormArrayContainer, FormArrayRow } from "components/cck/FormArray";
import CountrySelect from "components/selects/CountrySelect";
import ContactTypeSelect from "components/selects/ContactTypeSelect";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { Language } from "imports/Utils";

import { ShipSchemaInput, ShipSchemaOutput } from "view/ships/utils/ship.schema";

const ShipContactsForm = () => {

	const { control } = useFormContext<ShipSchemaInput, unknown, ShipSchemaOutput>();

	const { fields, append, remove } = useFieldArray({
		control,
		name: "contacts",
	});

	const contacts = useWatch({ control, name: "contacts" });

	const defaultContact = {
		id: null,
		type: "General",
		name: "",
		insertion: "",
		surName: "",
		email: "",
		telephone: "",
		fax: "",
		address: {
			name1: "",
			name2: "",
			street: "",
			zipCode: "",
			town: "",
			country: "NL",
		},
		rhineLicense: false,
		grootVaarbewijsA: false,
		grootVaarbewijsB: false,
		radarLicense: false,
	};

	return (
		<FormArrayContainer onAdd={() => append(defaultContact)}>
			{fields.map((item, index) => {
				return (
					<FormArrayRow
						key={item.id}
						title={Language("CONTACT")}
						subtitle={contacts?.[index]?.name}
						onDelete={() => remove(index)}>
						<Grid spacing={3} container>
							<Grid size={4}>
								<FormGroup label={Language("TYPE")} overflow="hidden">
									<Controller
										name={`contacts.${index}.type`}
										control={control}
										render={({ field }) => <ContactTypeSelect {...field} />} />
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("SHIPS_NAME")} overflow="hidden">
									<Controller
										name={`contacts.${index}.name`}
										control={control}
										render={({ field }) => <Input {...field} />} />
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("INSERTION")} overflow="hidden">
									<Controller
										name={`contacts.${index}.insertion`}
										control={control}
										render={({ field }) => <Input {...field} />} />
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("SURNAME")} overflow="hidden">
									<Controller
										name={`contacts.${index}.surName`}
										control={control}
										render={({ field }) => <Input {...field} />} />
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("EMAIL")} overflow="hidden">
									<Controller
										name={`contacts.${index}.email`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<Input {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("TELEPHONE")} overflow="hidden">
									<Controller
										name={`contacts.${index}.telephone`}
										control={control}
										render={({ field }) => <Input {...field} />} />
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("FAX")} overflow="hidden">
									<Controller
										name={`contacts.${index}.fax`}
										control={control}
										render={({ field }) => <Input {...field} />} />
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("ADDRESS_NAME_ONE")} overflow="hidden">
									<Controller
										name={`contacts.${index}.address.name1`}
										control={control}
										render={({ field }) => <Input {...field} />} />
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("ADDRESS_NAME_TWO")} overflow="hidden">
									<Controller
										name={`contacts.${index}.address.name2`}
										control={control}
										render={({ field }) => <Input {...field} />} />
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("ADDRESS")} overflow="hidden">
									<Controller
										name={`contacts.${index}.address.street`}
										control={control}
										render={({ field }) => <Input {...field} />} />
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("CLIENT_ZIPCODE")} overflow="hidden">
									<Controller
										name={`contacts.${index}.address.zipCode`}
										control={control}
										render={({ field }) => <Input {...field} />} />
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("CLIENT_CITY")} overflow="hidden">
									<Controller
										name={`contacts.${index}.address.town`}
										control={control}
										render={({ field }) => <Input {...field} />} />
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("CLIENT_COUNTRY")} overflow="hidden">
									<Controller
										name={`contacts.${index}.address.country`}
										control={control}
										render={({ field }) => <CountrySelect {...field} clearable />} />
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("RHINE_LICENSE")} overflow="hidden">
									<Controller
										name={`contacts.${index}.rhineLicense`}
										control={control}
										render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("LARGE_SAILING_LICENSE_A")} overflow="hidden">
									<Controller
										name={`contacts.${index}.grootVaarbewijsA`}
										control={control}
										render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("LARGE_SAILING_LICENSE_B")} overflow="hidden">
									<Controller
										name={`contacts.${index}.grootVaarbewijsB`}
										control={control}
										render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("RADAR_LICENSE")} overflow="hidden">
									<Controller
										name={`contacts.${index}.radarLicense`}
										control={control}
										render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
								</FormGroup>
							</Grid>
						</Grid>
					</FormArrayRow>
				);
			})}
		</FormArrayContainer>
	);
};

export default ShipContactsForm;
